import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Palette,
  Upload,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPut } from '../lib/api'

type ConfigMap = Record<string, string>

type FieldType = 'image' | 'color'

const FIELDS: ReadonlyArray<{
  key: string
  label: string
  hint: string
  type: FieldType
}> = [
  {
    key: 'Brand_LogoDesktop',
    label: 'Logo principal',
    hint: 'Se muestra en el header del sitio en escritorio. Recomendado: PNG transparente, ≥ 300px de ancho.',
    type: 'image',
  },
  {
    key: 'Brand_LogoMobile',
    label: 'Logo móvil',
    hint: 'Versión compacta para pantallas chicas (≤ 768px).',
    type: 'image',
  },
  {
    key: 'Brand_LogoFooter',
    label: 'Logo del footer',
    hint: 'Suele ser una variante en blanco/clara para fondo oscuro.',
    type: 'image',
  },
  {
    key: 'Brand_Favicon',
    label: 'Favicon',
    hint: 'Ícono de la pestaña del navegador. PNG cuadrado 32×32 o 64×64.',
    type: 'image',
  },
  {
    key: 'Brand_OgImage',
    label: 'Imagen Open Graph',
    hint: 'Se usa cuando alguien comparte la web en redes (1200×630 px recomendado).',
    type: 'image',
  },
  {
    key: 'Brand_ColorPrimary',
    label: 'Color primario',
    hint: 'Color principal de la marca (botones, acentos, links).',
    type: 'color',
  },
  {
    key: 'Brand_ColorSecondary',
    label: 'Color secundario',
    hint: 'Color de soporte (fondos oscuros, footer, textos secundarios).',
    type: 'color',
  },
  {
    key: 'Brand_ColorAccent',
    label: 'Color de acento',
    hint: 'Variante clara del primario. Usado en gradientes y hover de botones.',
    type: 'color',
  },
  {
    key: 'Brand_HeroDark1',
    label: 'Hero - Color 1 del gradient',
    hint: 'Primer color del gradient oscuro de los hero (login, registro, nosotros…).',
    type: 'color',
  },
  {
    key: 'Brand_HeroDark2',
    label: 'Hero - Color 2 del gradient',
    hint: 'Segundo color del gradient oscuro de los hero. Idealmente con tinte cálido.',
    type: 'color',
  },
  {
    key: 'Brand_Success',
    label: 'Color de éxito',
    hint: 'Verde para mensajes de OK y badges de status positivos.',
    type: 'color',
  },
  {
    key: 'Brand_Danger',
    label: 'Color de error',
    hint: 'Rojo para mensajes de error y badges de status negativos.',
    type: 'color',
  },
]

const BRAND_KEYS = FIELDS.map((f) => f.key)

function isValidHexColor(v: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(v.trim())
}

export function Branding() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<ConfigMap>({})
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)
  const [uploading, setUploading] = useState<Record<string, boolean>>({})
  const [uploadErr, setUploadErr] = useState<Record<string, string | null>>({})
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({})

  async function handleFilePick(key: string, file: File) {
    setUploading((u) => ({ ...u, [key]: true }))
    setUploadErr((e) => ({ ...e, [key]: null }))
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch('/api/admin/image-library', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      })
      const json = await res.json()
      if (!res.ok) {
        throw new Error(json?.error || `Error ${res.status}`)
      }
      const url: string | undefined = json?.data?.url
      if (!url) throw new Error('Respuesta sin url')
      update(key, url)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error subiendo imagen'
      setUploadErr((e) => ({ ...e, [key]: msg }))
    } finally {
      setUploading((u) => ({ ...u, [key]: false }))
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-config', 'branding'],
    queryFn: () =>
      apiGet<{ data: ConfigMap }>(`/api/admin/config?keys=${BRAND_KEYS.join(',')}`),
  })

  const original = useMemo<ConfigMap>(() => {
    const out: ConfigMap = {}
    const src = data?.data ?? {}
    for (const k of BRAND_KEYS) out[k] = src[k] ?? ''
    return out
  }, [data])

  useEffect(() => {
    setDraft((prev) => (Object.keys(prev).length === 0 ? { ...original } : prev))
  }, [original])

  const dirtyKeys = useMemo(
    () => BRAND_KEYS.filter((k) => (draft[k] ?? '') !== (original[k] ?? '')),
    [draft, original],
  )

  const saveMut = useMutation({
    mutationFn: async () => {
      const changes: ConfigMap = {}
      for (const k of dirtyKeys) changes[k] = draft[k] ?? ''
      return apiPut<{ updated: number }>('/api/admin/config', { changes })
    },
    onSuccess: (r) => {
      setFlash({ tipo: 'ok', msg: `${r.updated} valor(es) guardados` })
      qc.invalidateQueries({ queryKey: ['admin-config', 'branding'] })
      setTimeout(() => setFlash(null), 3000)
    },
    onError: (e: Error) =>
      setFlash({ tipo: 'err', msg: e?.message || 'Error al guardar' }),
  })

  function update(k: string, v: string) {
    setDraft((d) => ({ ...d, [k]: v }))
  }

  return (
    <>
      <Topbar
        title="Branding"
        subtitle="Logos, favicon, colores de marca y OG image — todo lo que se ve en el sitio público"
        actions={
          <>
            {dirtyKeys.length > 0 && (
              <Badge variant="warning">{dirtyKeys.length} sin guardar</Badge>
            )}
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={dirtyKeys.length === 0 || saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              Guardar cambios
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando branding…
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {FIELDS.map((f) => {
              const value = draft[f.key] ?? ''
              const isDirty = value !== (original[f.key] ?? '')
              return (
                <div
                  key={f.key}
                  className="rounded-2xl border border-ink-200 bg-white p-4"
                >
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-ink-900">
                          {f.label}
                        </h3>
                        {isDirty && <Badge variant="warning">modificado</Badge>}
                      </div>
                      <div className="font-mono text-[11px] text-ink-500">
                        {f.key}
                      </div>
                    </div>
                  </div>

                  <p className="mb-3 text-xs text-ink-500">{f.hint}</p>

                  {f.type === 'image' ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-lg border border-ink-200 bg-ink-50">
                          {value ? (
                            <img
                              src={value}
                              alt={f.label}
                              className="max-h-full max-w-full object-contain"
                              onError={(e) => {
                                ;(e.target as HTMLImageElement).style.display =
                                  'none'
                              }}
                            />
                          ) : (
                            <span className="text-[10px] text-ink-400">
                              sin imagen
                            </span>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col gap-2">
                          <input
                            type="url"
                            value={value}
                            placeholder="/ruta/al/archivo.png  o  https://…"
                            onChange={(e) => update(f.key, e.target.value)}
                            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              ref={(el) => {
                                fileInputs.current[f.key] = el
                              }}
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) handleFilePick(f.key, file)
                                e.target.value = ''
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => fileInputs.current[f.key]?.click()}
                              disabled={!!uploading[f.key]}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                            >
                              {uploading[f.key] ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <Upload className="size-3.5" />
                              )}
                              {uploading[f.key]
                                ? 'Subiendo…'
                                : 'Subir desde computadora'}
                            </button>
                            <span className="text-[11px] text-ink-400">
                              o pega una URL arriba
                            </span>
                          </div>
                        </div>
                      </div>
                      {uploadErr[f.key] && (
                        <div className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
                          <AlertCircle className="size-3.5" />
                          <span>{uploadErr[f.key]}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          type="color"
                          value={isValidHexColor(value) ? value : '#000000'}
                          onChange={(e) => update(f.key, e.target.value)}
                          className="size-12 cursor-pointer rounded-lg border border-ink-200 bg-white"
                        />
                      </div>
                      <input
                        type="text"
                        value={value}
                        placeholder="#rrggbb"
                        onChange={(e) => update(f.key, e.target.value)}
                        className={`flex-1 rounded-lg border bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500 ${
                          value && !isValidHexColor(value)
                            ? 'border-red-300'
                            : 'border-ink-200'
                        }`}
                      />
                      <Palette className="size-4 text-ink-400" />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
